import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { History, Mail, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = { envoi: 'Envoi', envoi_groupe: 'Envoi groupé', relance: 'Relance' };
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '-');

const EnvoiLine = ({ e, showSociete, testId }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs py-1.5 border-b border-gray-100 last:border-0" data-testid={testId}>
    {e.statut === 'echec'
      ? <XCircle className="h-3.5 w-3.5 text-red-500" />
      : <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />}
    <span className="font-mono text-gray-500">{fmt(e.shared_at)}</span>
    {showSociete && <span className="font-medium text-gray-800">{e.raison_sociale || e.souscription_id}</span>}
    <span className="flex items-center gap-1 text-gray-700"><Mail className="h-3 w-3" />{(e.recipients || []).join(', ')}</span>
    <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{TYPE_LABELS[e.type_envoi] || 'Envoi'}</span>
    <span className="text-gray-400">par {e.shared_by_email || '-'}</span>
  </li>
);

export const RecuEnvoisHistorique = ({ souscriptionId, refreshKey }) => {
  const { getAuthHeader } = useAuth();
  const [envois, setEnvois] = useState(null);

  useEffect(() => {
    axios.get(`${API}/souscriptions/admin/${souscriptionId}/envois`, { headers: getAuthHeader() })
      .then((r) => setEnvois(r.data.envois || []))
      .catch(() => setEnvois([]));
  }, [souscriptionId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50/60 p-3" data-testid={`historique-envois-${souscriptionId}`}>
      <h4 className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
        <History className="h-3.5 w-3.5" /> Historique des envois de reçus {envois ? `(${envois.length})` : ''}
      </h4>
      {envois === null && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      {envois?.length === 0 && <p className="text-xs text-gray-400">Aucun envoi enregistré</p>}
      {envois?.length > 0 && (
        <ul>{envois.map((e) => <EnvoiLine key={e.id} e={e} testId={`envoi-${e.id}`} />)}</ul>
      )}
    </div>
  );
};

export const JournalEnvoisDialog = ({ open, onOpenChange }) => {
  const { getAuthHeader } = useAuth();
  const [envois, setEnvois] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) return;
    setEnvois(null);
    axios.get(`${API}/souscriptions/admin/envois/journal`, { headers: getAuthHeader() })
      .then((r) => setEnvois(r.data.envois || []))
      .catch(() => setEnvois([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const term = q.trim().toLowerCase();
  const list = (envois || []).filter((e) => !term
    || `${e.raison_sociale} ${(e.recipients || []).join(' ')} ${e.numero_police || ''}`.toLowerCase().includes(term));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="journal-envois-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Journal des envois de reçus</DialogTitle></DialogHeader>
        <Input placeholder="Filtrer par société, email..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="journal-envois-search" />
        <div className="max-h-[60vh] overflow-y-auto">
          {envois === null && <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto my-6" />}
          {envois && list.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucun envoi</p>}
          <ul>{list.map((e) => <EnvoiLine key={e.id} e={e} showSociete testId={`journal-envoi-${e.id}`} />)}</ul>
        </div>
        <p className="text-xs text-gray-400" data-testid="journal-envois-count">{list.length} envoi(s)</p>
      </DialogContent>
    </Dialog>
  );
};

export const JournalEnvoisButton = ({ onClick }) => (
  <Button variant="outline" size="sm" onClick={onClick} className="gap-1.5 text-xs border-blue-200 text-blue-700" data-testid="journal-envois-btn">
    <History className="h-3.5 w-3.5" /> Journal des envois
  </Button>
);
